import type { MouseEvent, SyntheticEvent } from "react";
import React, { useEffect, useContext, Suspense, lazy } from "react";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "@litres-core/store";
import {
  checkIfRussianLanguage,
  deleteCookie,
  getCookie,
  hasWindow,
  setCookie,
} from "@litres/utils";
import { useAppSelector, api } from "@litres-core/store";
import { useTranslation } from "@litres-libs/i18n";
import { ModalOverlay } from "@litres/ui-kit/modal";
import { SkeletonBlock } from "@litres/ui-kit/skeleton";
import { EPlatform } from "@litres-libs/global-hooks";
import { useAbTest, useVarioqubTest } from "@litres-libs/experiments";
import { Logo } from "../Logo/Logo";
import { Modal } from "../Modal";
import { EModalIds } from "../Modal/types";
import { Search } from "../Search/Search";
import { SuggestionsSearch } from "../SuggestionsSearch";
import { ProfileButton } from "../User/ProfileButton";
import Login from "../User/Login/Login";
import { MyBooksIcon } from "../../../static/icons/MyBooksIcon";
import { trackerSendHeaderClick } from "../../utils/analytics";
import { Banners } from "../Banners/Banners";
import { HeaderContext } from "../../Header";
import { hideNewHeaderForLibrarian } from "../../utils/helpers";
import { IS_PHONE_ASKED_COOKIE, ONE_WEEK_IN_SEC } from "../../constants";
import { useShouldShowToggleBannner } from "../../hooks/useShouldShowToggleBannner";
import { useGetUserMonetizationData } from "../../hooks/useGetUserMonetizationData";
import { NavBarMobile } from "./NavBarMobile";
import { LowerMenuV2 } from "../LowerMenuV2";
import styles from "./HeaderMobile.module.scss";
import { Bonus } from "../Bonus/Bonus";

const AuthPopup = lazy(() => import("@litres/auth/popup"));
const ToggleBanner = lazy(() => import("../ToggleBanner/ToggleBanner"));
const AuthorizationPopup = lazy(
  () => import("@litres-modules/authorization-popup")
);

export const HeaderMobile = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const {
    isLibraryDomain,
    isSchoolDomain,
    isMainDomain,
    isPdaDomain,
    isSubscriptionDomain,
    isMobileOperatorDomain,
    isForeignDomain,
  } = useContext(HeaderContext);
  const isSubBannerVisible = useShouldShowToggleBannner({
    isSchoolDomain,
    isLibraryDomain,
    isForeignDomain,
    isSubscriptionDomain,
    isMobileOperatorDomain,
  });
  const {
    id,
    profile: { email, avatar_url: avatarUrl, registered_at, phone_number },
    library_info,
    socials,
    subscription: { is_active: isActiveSubscription },
    BSK,
    LKS,
  } = useAppSelector(state => state.foundationUser);
  const { data: userMonetizationData } = useGetUserMonetizationData();

  const isAbTest1128 = useAbTest(1128);
  const isBonusAbTest = useVarioqubTest("feature_web_llt190", "true");
  const isBonusesEnabledByCookie = [
    "true_with_onbording",
    "true_no_onbording",
  ].includes(getCookie("feature_web_llt190") || "");
  // Показываем бонусы только на ru доменах
  const shouldShowBonus =
    (isBonusAbTest || isBonusesEnabledByCookie) &&
    !isForeignDomain &&
    !isSchoolDomain;

  const {
    isOpen: isOpenLoginPopupError,
    errorText: loginPopupErrorText,
    uiOptions: loginPopupErrorUIOptions,
  } = useSelector(
    (state: RootState) => state.global.interface.loginPopupError
  ) ?? { isOpen: false, errorText: "" };

  const { isAuthPopupVisible: isShowModal } = useAppSelector(
    state => state.global.header.auth
  );

  const isUserAnonymous = id === 0;
  // Логика скопирована отсюда https://gitlab.srv.hub.litres.ru/platform/react/monorepo/-/blob/master/packages/modules/header-v3/src/components/User/User.tsx#L43
  const attachedSocnet = socials[0] || undefined;
  const hasTooManyEmptyFields =
    [phone_number, email, attachedSocnet].filter(Boolean).length <= 1;
  const isOneOfLibraryUser =
    library_info.is_library_user ||
    library_info.is_school_domain ||
    library_info.is_librarian;
  const isAsking =
    (!isUserAnonymous &&
      hasTooManyEmptyFields &&
      !isOneOfLibraryUser &&
      Boolean(getCookie("popup_asking"))) ||
    Boolean(getCookie("popup_after_auth"));

  const lang = useSelector<RootState, string>(
    state => state.face.language || "ru"
  );
  const shouldShowNewAuth = hasWindow
    ? /* ACQN-233: в личном кабинете можем поднимать popup для подтверждения телефона
    TODO: текущее состояние auth-popup не позволяет грамотно управлять подсостояниями
    */
      window.location.pathname === "/pages/personal_cabinet_login/" ||
      window.location.pathname === "/pages/my_settings/" ||
      Boolean(getCookie("new_auth")) ||
      Boolean(window.location.search.includes("new_auth=1")) ||
      isAbTest1128 ||
      checkIfRussianLanguage(lang) ||
      isForeignDomain
    : false;

  const booksCounter = isUserAnonymous
    ? BSK.length + LKS.length
    : Number(userMonetizationData?.cart_stats?.deferred_count ?? 0) +
      Number(userMonetizationData?.cart_stats?.items_count ?? 0) +
      Number(userMonetizationData?.cart_stats?.purchased_count ?? 0) +
      (userMonetizationData?.bought_collections.length || 0);
  const myBooksCounter = booksCounter > 9 ? "9+" : booksCounter;

  const isSchoolOrBiblioDomain = isSchoolDomain || isLibraryDomain;
  const resetStoreInterfaceRequests = (): void => {
    dispatch(
      api.global.interfaceRequests.setLoginPopupError({
        isOpen: false,
        errorText: "",
      })
    );
  };

  hideNewHeaderForLibrarian(library_info.is_librarian || false, id === 0, "ru");

  useEffect(() => {
    const isPhoneAskingBlockedByQueryParam = new URLSearchParams(
      window.location.search
    ).has("block-phone-asking");
    if (isOpenLoginPopupError && !isPhoneAskingBlockedByQueryParam) {
      openAuthModal();
    }
  }, [isOpenLoginPopupError]);

  const openAuthModal = (): void => {
    dispatch(api.global.header.auth.setAuthPopupVisibility(true));
  };

  const closeAuthModal = (): void => {
    if (!isUserAnonymous && shouldShowNewAuth) {
      // для зарегистрированного пользователя при закрытии окна Привязать телефон, ставим куку на неделю
      // когда кука сгорит спрашиваем телефон у пользователя повторно.
      // только для новой авторизации
      setCookie(IS_PHONE_ASKED_COOKIE, "1", { "max-age": ONE_WEEK_IN_SEC });
    }
    dispatch(api.global.auth.setRateLimitError(false));

    dispatch(api.global.header.auth.setAuthPopupVisibility(false));

    if (isOpenLoginPopupError) {
      resetStoreInterfaceRequests();
    }
    if (isAsking) {
      deleteCookie("popup_asking");
    }
  };

  const handleAvatarClick = (event: MouseEvent<HTMLElement>) => {
    if (isUserAnonymous) {
      event.preventDefault();
      trackerSendHeaderClick("Войти", hasWindow ? window.location.href : "");
      openAuthModal();
    }
  };

  useEffect(() => {
    const checkOldUserReminder = getCookie("checkOldUserReminder");
    if (checkOldUserReminder && registered_at) {
      deleteCookie("checkOldUserReminder");
    }
  }, []);

  const handleMenuItemClick = (
    e: SyntheticEvent,
    buttonTitle: string,
    href: string
  ) => {
    e.preventDefault();
    trackerSendHeaderClick(buttonTitle, hasWindow ? window.location.href : "");
    if (hasWindow) {
      window.location.href = href;
    }
  };

  return (
    <header
      className={classnames(styles.wrapper)}
      data-testid="header"
      role="banner"
    >
      <NavBarMobile />
      <Banners />
      {shouldShowNewAuth ? (
        <ModalOverlay
          isOpen={isShowModal}
          onClose={closeAuthModal}
          isMobile
          closeOnClickOutside={
            !loginPopupErrorUIOptions?.noCancel &&
            !loginPopupErrorUIOptions?.fullScreen
          }
          isFullScreenMode={loginPopupErrorUIOptions?.fullScreen}
          isCloseButtonShown={!loginPopupErrorUIOptions?.noCancel}
        >
          <Suspense fallback={<SkeletonBlock height="100%" width="100%" />}>
            <AuthPopup
              isMobile
              hidePopup={closeAuthModal}
              errorTitle={t(loginPopupErrorText ?? "")}
              isLibrary={isSchoolOrBiblioDomain}
              uiOptions={loginPopupErrorUIOptions}
            />
          </Suspense>
        </ModalOverlay>
      ) : (
        <>
          {isShowModal && (
            <Modal id={EModalIds.auth}>
              <Suspense fallback={<SkeletonBlock height="100%" width="100%" />}>
                <AuthorizationPopup
                  isMobile
                  hidePopup={closeAuthModal}
                  errorTitle={loginPopupErrorText}
                  isLibrary={isSchoolOrBiblioDomain}
                  showSocials={!isSchoolOrBiblioDomain}
                  showPhoneButton={!isSchoolOrBiblioDomain}
                  showAppLoginButton={!isSchoolOrBiblioDomain}
                  showLibraryTicketButton={!isSchoolOrBiblioDomain}
                  showGDPRAgreement={!isSchoolOrBiblioDomain}
                  customTitle={
                    isSchoolOrBiblioDomain
                      ? "Вход для читателей и библиотекарей"
                      : ""
                  }
                />
              </Suspense>
            </Modal>
          )}
        </>
      )}

      <div className={styles.container}>
        <div className={styles.panel}>
          <div className={styles.logo}>
            <Logo />
          </div>
          {shouldShowBonus && <Bonus isMobile />}
          <div onClick={handleAvatarClick}>
            {isUserAnonymous ? (
              <div data-testid="header__login-button--mobile">
                <Login className={styles.tabWrap} />
              </div>
            ) : (
              <div data-testid="header__login-button--mobile">
                <ProfileButton
                  className={styles.tabWrap}
                  id={Number(id)}
                  href="/me/profile/"
                  size={50}
                  hasAvatar={Boolean(avatarUrl)}
                  isGradient={Boolean(isActiveSubscription)}
                  isPDA
                />
              </div>
            )}
          </div>

          {!shouldShowBonus && (
            <div
              className={styles.control}
              // data-counter={booksCounter > 0 ? myBooksCounter : null}
              data-testid="my-books__button"
            >
              <button
                type="button"
                className={styles.booksButton}
                onClick={(e: SyntheticEvent) => {
                  handleMenuItemClick(e, "Мои книги", "/my-books");
                }}
              >
                <div
                  className={classnames(styles.icon, {
                    [styles.icon__twoDigitCounter]: booksCounter > 9,
                  })}
                  data-counter={booksCounter > 0 ? myBooksCounter : null}
                >
                  <MyBooksIcon />
                </div>
                <div>{t("Мои книги")}</div>
              </button>
            </div>
          )}
        </div>
        <div className={styles.searchContainer}>
          {/*
            поиск по подсказкам (mobile) или спотлайт
            https://docs.litres.ru/pages/viewpage.action?pageId=98630557
          */}
          {isLibraryDomain ||
          isMobileOperatorDomain ||
          isSubscriptionDomain ||
          isMainDomain ||
          isPdaDomain ? (
            <SuggestionsSearch platform={EPlatform.mobile} />
          ) : (
            <Search isDesktop={false} />
          )}
        </div>
      </div>
      <LowerMenuV2 isPDA />
      {isSubBannerVisible && (
        <Suspense fallback={<SkeletonBlock width="100%" height={50} />}>
          <ToggleBanner />
        </Suspense>
      )}
    </header>
  );
};
